import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button, Input, Card, CardContent } from '@leoclinic/ui';
import { useAuth } from '@leoclinic/hooks';
import { authService } from '@leoclinic/services';
import { BRAND_COLORS } from '@leoclinic/types';
import { loginSchema, type LoginFormData } from '../schemas/auth.schema';
import { useClinicPortal } from '@/providers/clinic-provider';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clinic } = useClinicPortal();
  const { signIn, isSigningIn } = useAuth();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/minhas-marcacoes';

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resendSent, setResendSent] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: LoginFormData) => {
    setErrorMessage(null);
    setPendingEmail(null);
    setResendSent(false);

    try {
      await signIn(data);
      navigate(from, { replace: true });
    } catch (err) {
      const message = authService.getAuthErrorMessage(err);
      if (message === 'EMAIL_NOT_CONFIRMED') {
        setPendingEmail(data.email);
        setErrorMessage('Confirme o email antes de entrar. Verifique a caixa de entrada (e spam).');
        return;
      }
      setErrorMessage(message);
    }
  };

  const handleResend = async () => {
    const email = pendingEmail ?? getValues('email');
    if (!email) return;
    setIsResending(true);
    try {
      await authService.resendSignupConfirmation(email);
      setResendSent(true);
    } catch {
      setErrorMessage('Não foi possível reenviar. Tente mais tarde.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F3EE] flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-[#ECECEC]">
        <CardContent className="pt-8 pb-8 space-y-6">
          <div className="text-center space-y-1">
            <h1 className="text-xl font-bold" style={{ color: BRAND_COLORS.greenDark }}>
              Entrar
            </h1>
            <p className="text-sm text-muted-foreground">
              Aceda à sua área de cliente — {clinic?.name ?? 'a clínica'}
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Email"
              type="email"
              placeholder="nome@email.com"
              error={errors.email?.message}
              {...register('email')}
            />
            <Input
              label="Password"
              type="password"
              placeholder="••••••••"
              error={errors.password?.message}
              {...register('password')}
            />
            {errorMessage && (
              <div className="space-y-2">
                <p className="text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
                  {errorMessage}
                </p>
                {pendingEmail && !resendSent && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={isResending}
                    onClick={handleResend}
                  >
                    {isResending ? 'A enviar...' : 'Reenviar email de confirmação'}
                  </Button>
                )}
                {resendSent && (
                  <p className="text-sm text-center" style={{ color: BRAND_COLORS.greenDark }}>
                    Email reenviado. Verifique a sua caixa de entrada.
                  </p>
                )}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={isSigningIn}>
              {isSigningIn ? 'A entrar...' : 'Entrar'}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground">
            Ainda não tem conta?{' '}
            <Link to="/registo" className="font-medium hover:underline" style={{ color: BRAND_COLORS.greenDark }}>
              Criar conta
            </Link>
          </p>
          <p className="text-center text-sm">
            <Link to="/" className="text-muted-foreground hover:underline">
              ← Voltar à marcação
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
